'use client'

import { motion } from 'motion/react'
import { useEffect, useState } from 'react'

import { cn } from '@/utils'

interface SpreadsheetBackgroundProps {
  className?: string
}

export function SpreadsheetBackground({
  className = '',
}: SpreadsheetBackgroundProps) {
  const verticalLines = Array.from({ length: 40 }).map((_, i) => i * 200)
  const horizontalLines = Array.from({ length: 50 }).map((_, i) => i * 40)

  return (
    <div className={cn('absolute inset-0 z-0 pointer-events-none', className)}>
      <div
        className="absolute inset-0"
        style={{
          maskImage:
            'radial-gradient(ellipse 80% 80% at 50% 0%, #000 40%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 80% at 50% 0%, #000 40%, transparent 100%)',
          maskPosition: 'center top',
          WebkitMaskPosition: 'center top',
        }}
      >
        <svg
          role="img"
          aria-label="Spreadsheet background"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g
            className="stroke-slate-300/50"
            strokeWidth="1"
            strokeDasharray="4 6"
          >
            <motion.g>
              {verticalLines.map((offset, i) => (
                <g key={`v-${i}`}>
                  <motion.line
                    x1={`calc(50% + ${offset}px - 100px)`}
                    y1="0"
                    x2={`calc(50% + ${offset}px - 100px)`}
                    y2="100%"
                    initial={{ y2: '0%' }}
                    whileInView={{ y2: '100%' }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 2.5,
                      delay: i * 0.1,
                      ease: 'easeOut',
                    }}
                  />
                  {offset !== 0 && (
                    <motion.line
                      x1={`calc(50% - ${offset}px - 100px)`}
                      y1="0"
                      x2={`calc(50% - ${offset}px - 100px)`}
                      y2="100%"
                      initial={{ y2: '0%' }}
                      whileInView={{ y2: '100%' }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 2.5,
                        delay: i * 0.1,
                        ease: 'easeOut',
                      }}
                    />
                  )}
                </g>
              ))}
            </motion.g>

            <motion.g>
              {horizontalLines.map((y, i) => (
                <motion.line
                  key={`h-${i}`}
                  x1="0"
                  y1={y}
                  x2="100%"
                  y2={y}
                  initial={{ x2: '0%' }}
                  whileInView={{ x2: '100%' }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 2.5,
                    delay: 0.5 + i * 0.1,
                    ease: 'easeOut',
                  }}
                />
              ))}
            </motion.g>
          </g>
        </svg>
      </div>

      <AnimatedGridPattern />
    </div>
  )
}

interface Cell {
  id: number
  x: number
  y: number
}

function AnimatedGridPattern() {
  const [cells, setCells] = useState<Cell[]>([])

  useEffect(() => {
    const newCells: Cell[] = Array.from({ length: 15 }).map((_, i) => ({
      id: i,
      x: Math.floor(Math.random() * 20) * 200,
      y: Math.floor(Math.random() * 20) * 40,
    }))
    setCells(newCells)
  }, [])

  return (
    <div className="absolute inset-0 overflow-hidden mask-image-fade">
      {cells.map((cell) => (
        <motion.div
          key={cell.id}
          className="absolute bg-brand/5 border border-brand/10"
          style={{
            width: 200,
            height: 40,
            left: '50%',
            marginLeft: cell.x - 2000 - 100,
            top: cell.y,
          }}
          initial={{ opacity: 0 }}
          whileInView={{
            opacity: [0, 1, 0],
          }}
          viewport={{ once: true }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatDelay: Math.random() * 5 + 2,
            ease: 'easeInOut',
            delay: Math.random() * 5,
          }}
        />
      ))}
    </div>
  )
}
